import React, { useState, useRef } from 'react';
import { renderRichText } from 'gatsby-source-contentful/rich-text';
import XUpCardsSection from './XUpCardsSection';
import SideBySideSection from './SideBySideSection';
import TilesSection from './TilesSection';
import TextAlignment from '../../components/TextAlignment';
import CategoryNav from '../../components/CategoryNav';
import useWindowSize from '../../utils/useWindowSize';
import getThemeClass from '../../utils/getThemeClass';
import styles from './CategorySection.module.css';

/**
 * @param {  HTMLElement } el
 * @param {number} headerHeight
 */
function getOffset(el, headerHeight) {
  const rect = el.getBoundingClientRect();
  return {
    left: rect.left + window.scrollX,
    top: rect.top + window.scrollY - headerHeight,
  };
}

/** @param {FF.ContentfulCategorySectionProps} props */

function CategorySection({
  categorySlide,
  entryTitle,
  title,
  subtitle,
  categoryNavBGColor,
  backgroundColor,
  isEmbedded,
}) {
  /**  @type {React.MutableRefObject<HTMLDivElement|null>} sectionRef */
  const sectionRef = useRef(null);
  if (categorySlide.length === 5) {
    throw new Error(
      `Category Section failed -- section "${entryTitle}" cannot have 5 or 1 cards. Valid options are: 2,3,4,6`,
    );
  }
  const slideTitles = Array.from(categorySlide, slide => slide.title);
  const [selectedCategory, setCategory] = useState(slideTitles[0]);
  const { headerHeight = 86 } = useWindowSize() || {};
  return (
    <div
      ref={sectionRef}
      className={`${styles.categorySectionWrapper} ${getThemeClass(
        categoryNavBGColor || backgroundColor || 'White 100 (#FFFFFF)',
      )} ${isEmbedded ? styles.categorySectionEmbedded : ''}`}
    >
      {(title || subtitle) && (
        <TextAlignment alignment="Center" className={styles.headerWrapper}>
          {title && <h2>{title}</h2>}
          {subtitle && <p>{subtitle.subtitle}</p>}
        </TextAlignment>
      )}
      <CategoryNav
        selectedCategory={selectedCategory}
        setSelectedCategory={category => {
          setCategory(category);
          const elBox = sectionRef.current;
          if (elBox && headerHeight && window.scrollY > elBox.offsetTop) {
            window.scrollTo(
              getOffset(elBox, headerHeight).left,
              getOffset(elBox, headerHeight).top,
            );
          }
        }}
        sortedCategories={slideTitles}
        className={`${styles.categorySectionNavWrapper} ${getThemeClass(
          categoryNavBGColor || backgroundColor || 'White 100 (#FFFFFF)',
        )}`}
      />
      <div className={styles.categoryWrapper}>
        {categorySlide.map(currentCategory => {
          if (currentCategory.title === selectedCategory) {
            switch (currentCategory.slide.__typename) {
              case 'ContentfulBlockSection':
                return (
                  <TextAlignment
                    alignment={currentCategory.slide.textAlignment}
                    className={styles.pageWidth}
                    key={currentCategory.title}
                  >
                    <div>{renderRichText(currentCategory.slide.body)}</div>
                  </TextAlignment>
                );

              case 'ContentfulSideBySideSection':
                return (
                  <SideBySideSection
                    {...currentCategory.slide}
                    className={styles.pageWidth}
                    key={currentCategory.title}
                  />
                );

              case 'ContentfulXUpCardsSection':
                return (
                  <XUpCardsSection
                    {...currentCategory.slide}
                    className={styles.pageWidth}
                    key={currentCategory.title}
                  />
                );
              case 'ContentfulTilesSection':
                return (
                  <TilesSection
                    {...currentCategory.slide}
                    className={styles.pageWidth}
                    key={currentCategory.title}
                  />
                );
              default:
                return null;
            }
          }
          return null;
        })}
      </div>
    </div>
  );
}
export default CategorySection;
